import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck,
  Search,
  RefreshCcw,
  XCircle,
  ArrowUpRight,
  Loader2,
  BadgeCheck,
  BadgeAlert,
  Pencil,
  Trash2,
  Save,
  X,
} from 'lucide-react';
import { adminUsersAPI } from '../api';

const approvalOptions = ['pending', 'approved', 'rejected'];

const getCategories = (row) => {
  const payloadCategories = row?.payload?.overlap_categories;
  return Array.isArray(row?.overlap_categories)
    ? row.overlap_categories
    : Array.isArray(payloadCategories)
      ? payloadCategories
      : [];
};

const Affiliates = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [enrollingId, setEnrollingId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editDrafts, setEditDrafts] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const categoryNames = useMemo(() => categories.map((category) => category.name), [categories]);

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError('');

      const [response, categoryResponse] = await Promise.all([
        adminUsersAPI.signupSubmissions({
          type: 'affiliate',
          limit: 200,
          ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
        }),
        adminUsersAPI.overlapCategories(),
      ]);

      setRows(response?.data?.submissions || []);
      setCategories(categoryResponse?.data?.categories || []);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load affiliate signups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [searchTerm]);

  const handleEnroll = async (row) => {
    try {
      setEnrollingId(row.id);
      setRowErrors((prev) => ({ ...prev, [row.id]: '' }));
      const response = await adminUsersAPI.enrollSignupSubmission(row.id);
      const enrolled = response?.data?.submission || response?.submission || response?.data || response || {};
      setRows((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...enrolled } : item)));
    } catch (requestError) {
      setRowErrors((prev) => ({ ...prev, [row.id]: requestError.message || 'Failed to enroll this signup submission' }));
    } finally {
      setEnrollingId(null);
    }
  };

  const openEditor = (row) => {
    if (!row.user_id) return;

    setEditingUserId(row.user_id);
    setEditDrafts((prev) => ({
      ...prev,
      [row.user_id]: prev[row.user_id] || {
        name: row.name || '',
        company: row.company || '',
        approval_status: row.approval_status || 'approved',
        active: row.active !== false,
        overlap_categories: getCategories(row),
      },
    }));
  };

  const cancelEditor = () => setEditingUserId(null);

  const updateDraft = (userId, field, value) => {
    setEditDrafts((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), [field]: value },
    }));
  };

  const toggleDraftCategory = (userId, categoryName) => {
    const draft = editDrafts[userId] || {};
    const current = Array.isArray(draft.overlap_categories) ? draft.overlap_categories : [];
    updateDraft(
      userId,
      'overlap_categories',
      current.includes(categoryName) ? current.filter((item) => item !== categoryName) : [...current, categoryName]
    );
  };

  const saveUser = async (row) => {
    try {
      setSavingUserId(row.user_id);
      await adminUsersAPI.update(row.user_id, editDrafts[row.user_id] || {});
      await fetchRows();
      setEditingUserId(null);
    } catch (requestError) {
      setRowErrors((prev) => ({ ...prev, [row.id]: requestError.message || 'Failed to save user' }));
    } finally {
      setSavingUserId(null);
    }
  };

  const deleteUser = async (row) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;

    try {
      setDeletingUserId(row.user_id);
      await adminUsersAPI.delete(row.user_id);
      await fetchRows();
    } catch (requestError) {
      setRowErrors((prev) => ({ ...prev, [row.id]: requestError.message || 'Failed to delete user' }));
    } finally {
      setDeletingUserId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Affiliate Signups</h2>
          <p className="text-slate-400 mt-2">Review captured affiliate signup details and overlap context</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/discovery?pipeline=affiliate&tab=qualified&origin=signup" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors flex items-center">
            Open in Lead Intelligence
          </Link>
          <button onClick={fetchRows} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg transition-colors flex items-center">
            <RefreshCcw className="w-4 h-4 mr-2" />Refresh
          </button>
        </div>
      </div>

      <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-100 mb-4">Applications ({rows.length})</h3>

          {loading ? (
            <div className="h-80 flex items-center justify-center text-slate-400">Loading affiliate signup submissions...</div>
          ) : error ? (
            <div className="h-80 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="h-80 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg text-slate-400">
              <div className="text-center">
                <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                No affiliate signup submissions yet.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((row) => {
                const payload = row.payload || {};
                const company = row.company || payload.company || '-';
                const contactName = row.name || payload.name || [payload.first_name, payload.last_name].filter(Boolean).join(' ') || '-';
                const contactEmail = row.email || payload.email || '-';
                const userCategories = getCategories(row);
                const website = payload.website || '-';
                const isEnrolled = Boolean(row.enrolled_lead_id);
                const deepLink = row.enrolled_lead_id
                  ? `/discovery?pipeline=affiliate&tab=qualified&origin=signup&leadId=${encodeURIComponent(row.enrolled_lead_id)}`
                  : '/discovery?pipeline=affiliate&tab=qualified&origin=signup';
                const hasUser = Boolean(row.user_id);
                const draft = editDrafts[row.user_id] || {};
                const isEditing = editingUserId === row.user_id;

                return (
                  <div key={row.id} className="rounded-xl border border-slate-700 bg-slate-800/30 p-4 space-y-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-slate-100 font-semibold">{company}</p>
                        <p className="text-slate-400 text-sm">{contactName} · {contactEmail}</p>
                        <p className="text-slate-500 text-sm">Website: {website}</p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {isEnrolled && (
                          <Link to={deepLink} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-emerald-900/40 border border-emerald-700/60 text-emerald-200 text-sm">
                            <ArrowUpRight className="w-4 h-4 mr-1" />Open Lead
                          </Link>
                        )}
                        {!isEnrolled && (
                          <button onClick={() => handleEnroll(row)} disabled={enrollingId === row.id} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm">
                            {enrollingId === row.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                            Enroll
                          </button>
                        )}
                        {hasUser && !isEditing && (
                          <button onClick={() => openEditor(row)} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">
                            <Pencil className="w-4 h-4 mr-1" />Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {rowErrors[row.id] ? <p className="text-sm text-red-300">{rowErrors[row.id]}</p> : null}

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Status</p>
                        <div className="mt-1 flex items-center gap-2 text-slate-200 capitalize">
                          {isEnrolled ? <BadgeCheck className="w-4 h-4 text-emerald-400" /> : <BadgeAlert className="w-4 h-4 text-amber-400" />}
                          {isEnrolled ? 'Enrolled' : 'Pending'}
                        </div>
                      </div>
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Overlap categories</p>
                        <p className="mt-1 text-slate-200">{userCategories.length ? userCategories.join(', ') : '-'}</p>
                      </div>
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Row user</p>
                        <p className="mt-1 text-slate-200">{row.user_id || '-'}</p>
                      </div>
                      <div className="rounded-lg bg-slate-900/50 border border-slate-700 p-3">
                        <p className="text-slate-500">Signup ID</p>
                        <p className="mt-1 text-slate-200">{row.id}</p>
                      </div>
                    </div>

                    {hasUser && isEditing ? (
                      <div className="rounded-xl border border-slate-700 bg-slate-950/30 p-4 space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-sm text-slate-300">Name</span>
                            <input value={draft.name || ''} onChange={(e) => updateDraft(row.user_id, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100" />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm text-slate-300">Company</span>
                            <input value={draft.company || ''} onChange={(e) => updateDraft(row.user_id, 'company', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100" />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm text-slate-300">Approval status</span>
                            <select value={draft.approval_status || 'approved'} onChange={(e) => updateDraft(row.user_id, 'approval_status', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-100">
                              {approvalOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                          </label>
                          <div className="space-y-2">
                            <span className="text-sm text-slate-300">Active</span>
                            <button type="button" onClick={() => updateDraft(row.user_id, 'active', !(draft.active !== false))} className={`w-full px-3 py-2 rounded-lg border text-sm ${draft.active !== false ? 'bg-emerald-900/30 border-emerald-700 text-emerald-200' : 'bg-slate-800 border-slate-600 text-slate-200'}`}>
                              {draft.active !== false ? 'Deactivate' : 'Reactivate'}
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <span className="text-sm text-slate-300">Overlap categories</span>
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {categoryNames.map((categoryName) => {
                              const selected = (draft.overlap_categories || []).includes(categoryName);
                              return (
                                <button key={categoryName} type="button" onClick={() => toggleDraftCategory(row.user_id, categoryName)} className={`px-3 py-2 rounded-lg border text-left text-sm ${selected ? 'bg-red-600/20 border-red-500/60 text-red-200' : 'bg-slate-800 border-slate-600 text-slate-200'}`}>
                                  {categoryName}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2">
                          <button onClick={cancelEditor} className="inline-flex items-center px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm">
                            <X className="w-4 h-4 mr-1" />Cancel
                          </button>
                          <button onClick={() => saveUser(row)} disabled={savingUserId === row.user_id} className="inline-flex items-center px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm">
                            {savingUserId === row.user_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                            Save
                          </button>
                          <button onClick={() => deleteUser(row)} disabled={deletingUserId === row.user_id} className="inline-flex items-center px-3 py-2 rounded-lg bg-red-900/40 hover:bg-red-800/50 border border-red-700/60 text-red-200 text-sm disabled:opacity-60">
                            <Trash2 className="w-4 h-4 mr-1" />Delete user
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Affiliates;
